package com.fantasytenis.shared.data.tennis.dto

import kotlinx.serialization.Serializable

/**
 * Player costs DTO
 */
@Serializable
data class PlayerCostsDto(
    val serve: Int,
    val forehand: Int,
    val backhand: Int,
    val volleys: Int,
    val fighting: Int
)

/**
 * Tennis Player DTO
 */
@Serializable
data class TennisPlayerDto(
    val id: String,
    val externalId: String,
    val name: String,
    val firstName: String? = null,
    val lastName: String? = null,
    val country: String,
    val atpRanking: Int? = null,
    val eloRating: Double? = null,
    val gender: String = "M",
    val costs: PlayerCostsDto,
    val isActive: Boolean = true,
    val avatarUrl: String? = null
)

/**
 * Period score for a single set in a match
 */
@Serializable
data class PeriodScoreDto(
    val homeScore: Int,
    val awayScore: Int,
    val setNumber: Int? = null
)

/**
 * Live Match DTO
 */
@Serializable
data class LiveMatchDto(
    val id: String,
    val externalId: String,
    val tournamentId: String,
    val player1Id: String? = null,
    val player2Id: String? = null,
    val player1Name: String? = null,
    val player2Name: String? = null,
    val homeScore: Int? = null,
    val awayScore: Int? = null,
    val winnerId: String? = null,
    val status: String,
    val matchStatus: String? = null,
    val round: String? = null,
    val competition: String? = null,
    val scheduledTime: String,
    val setsPlayed: Int = 0,
    val hasStatistics: Boolean = false,
    val periodScores: List<PeriodScoreDto> = emptyList()
)

/**
 * Match Statistics DTO
 */
@Serializable
data class MatchStatisticsDto(
    val id: String,
    val matchId: String,
    val playerId: String,
    // Service stats
    val aces: Int = 0,
    val doubleFaults: Int = 0,
    val firstServePct: Double? = null,
    val servePoints: Int = 0,
    val servePointsWon: Int = 0,
    // Winners/Errors
    val forehandWinners: Int = 0,
    val forehandErrors: Int = 0,
    val backhandWinners: Int = 0,
    val backhandErrors: Int = 0,
    val volleyWinners: Int = 0,
    val totalWinners: Int = 0,
    val totalUnforcedErrors: Int = 0,
    // Break points
    val breakPointsConverted: Int = 0,
    val breakPointsSaved: Int = 0,
    val breakPointsFaced: Int = 0,
    // Match info
    val setsPlayed: Int = 0,
    val totalPointsPlayed: Int = 0,
    val isWinner: Boolean = false
)

/**
 * Tournament DTO
 */
@Serializable
data class TournamentDto(
    val id: String,
    val externalId: String,
    val name: String,
    val location: String? = null,
    val country: String? = null,
    val level: String,
    val surface: String,
    val gender: String = "M",
    val startDate: String,
    val endDate: String,
    val prizeMoney: Double? = null,
    val bestOf: Int = 3,
    val isActive: Boolean = true
)
